import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Users, UserPlus, Search, Trash2, Mail, MapPin, Calendar, Copy, Check, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthors = () => {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    newThisMonth: 0,
    claimedArticles: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Add Author state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newAuthor, setNewAuthor] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    town: '',
    country: '',
    occupation: '',
    researcher_role: '',
    custom_password: ''
  });
  const [tempPassword, setTempPassword] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  
  // Edit Author state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    town: '',
    country: '',
    occupation: '',
    researcher_role: ''
  });

  useEffect(() => {
    checkAuth();
    loadAuthors();
    loadStats();
  }, []);

  const checkAuth = () => {
    const isAdmin = sessionStorage.getItem('admin_authenticated');
    if (!isAdmin) {
      navigate('/admin/login');
    }
  };

  const loadAuthors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/authors`);
      if (!response.ok) throw new Error('Failed to load authors');
      
      const data = await response.json();
      setAuthors(data);
    } catch (error) {
      void 0;
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/author-stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      void 0;
    }
  };

  const handleAddAuthor = async () => {
    // Validate required fields
    if (!newAuthor.username || !newAuthor.email || !newAuthor.country) {
      toast.error('Please fill required fields: username, email, country');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/authors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAuthor)
      });
      
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to create author');
      }
      
      const result = await response.json();
      
      setTempPassword(result.temporary_password);
      setPasswordCopied(false);
      
      toast.success(`Author ${result.user.username} created successfully!`);
      loadAuthors();
      loadStats();
      
      // Reset form but keep dialog open to show password
      setNewAuthor({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        town: '',
        country: '',
        occupation: '',
        researcher_role: '',
        custom_password: ''
      });
    } catch (error) {
      toast.error(error.message || 'Failed to create author');
    }
  };
  
  const handleDeleteAuthor = async (userId, username) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/authors/${userId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to delete author');
      }
      
      const result = await response.json();
      
      toast.success(`Author ${username} deleted successfully`);
      loadAuthors();
      loadStats();
    } catch (error) {
      toast.error(error.message || 'Failed to delete author');
    }
  };
  
  const copyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      setPasswordCopied(true);
      toast.success('Password copied to clipboard!');
      setTimeout(() => setPasswordCopied(false), 3000);
    }
  };

  
  const openEditDialog = (author) => {
    setEditingAuthor(author);
    setEditForm({
      username: author.username || '',
      email: author.email || '',
      first_name: author.first_name || '',
      last_name: author.last_name || '',
      middle_name: author.middle_name || '',
      town: author.town || '',
      country: author.country || '',
      occupation: author.occupation || '',
      researcher_role: author.researcher_role || ''
    });
    setIsEditDialogOpen(true);
  };
  
  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingAuthor(null);
  };
  
  const handleUpdateAuthor = async () => {
    // Validate required fields
    if (!editForm.username || !editForm.email || !editForm.country) {
      toast.error('Please fill required fields: username, email, country');
      return;
    }
    
    try {
      const authorId = editingAuthor.user_id || editingAuthor.id;
      const response = await fetch(`${API_URL}/api/admin/authors/${authorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to update author');
      }
      
      toast.success(`Author ${result.user.username} updated successfully!`);
      closeEditDialog();
      loadAuthors();
      loadStats();
    } catch (error) {
      toast.error(error.message || 'Failed to update author');
    }
  };

  
  const closeAddDialog = () => {
    setIsAddDialogOpen(false);
    setTempPassword(null);
    setPasswordCopied(false);
  };

  useEffect(() => {
    loadAuthors();
    loadStats();
  }, []);

  const filteredAuthors = authors.filter((author) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      author.username?.toLowerCase().includes(query) ||
      author.email?.toLowerCase().includes(query) ||
      author.first_name?.toLowerCase().includes(query) ||
      author.last_name?.toLowerCase().includes(query) ||
      author.town?.toLowerCase().includes(query) ||
      author.country?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-8 w-8 text-blue-600" />
              Registered Authors
            </h1>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
              <p className="text-sm text-slate-600 mt-1">Total Authors</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-green-600">{stats.newThisMonth}</div>
              <p className="text-sm text-slate-600 mt-1">New This Month</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-purple-600">{stats.claimedArticles}</div>
              <p className="text-sm text-slate-600 mt-1">Total Claimed Articles</p>
            </CardContent>
          </Card>
        </div>

        {/* Search and Add Button */}
        <div className="flex items-center gap-4 mb-6">
          <Card className="flex-1">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Search by name, email, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 border-0 focus-visible:ring-0"
                />
              </div>
            </CardContent>
          </Card>
          
          <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
            if (!open) closeAddDialog();
            setIsAddDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Author
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">Add New Author</DialogTitle>
              </DialogHeader>
              
              {!tempPassword ? (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="username">Username *</Label>
                      <Input
                        id="username"
                        value={newAuthor.username}
                        onChange={(e) => setNewAuthor({...newAuthor, username: e.target.value})}
                        placeholder="johndoe"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newAuthor.email}
                        onChange={(e) => setNewAuthor({...newAuthor, email: e.target.value})}
                        placeholder="john@university.edu"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={newAuthor.first_name}
                        onChange={(e) => setNewAuthor({...newAuthor, first_name: e.target.value})}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label htmlFor="middle_name">Middle Name</Label>
                      <Input
                        id="middle_name"
                        value={newAuthor.middle_name}
                        onChange={(e) => setNewAuthor({...newAuthor, middle_name: e.target.value})}
                        placeholder="M"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={newAuthor.last_name}
                        onChange={(e) => setNewAuthor({...newAuthor, last_name: e.target.value})}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="town">Town/City</Label>
                      <Input
                        id="town"
                        value={newAuthor.town}
                        onChange={(e) => setNewAuthor({...newAuthor, town: e.target.value})}
                        placeholder="Sofia"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        value={newAuthor.country}
                        onChange={(e) => setNewAuthor({...newAuthor, country: e.target.value})}
                        placeholder="Bulgaria"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="occupation">Occupation</Label>
                    <Input
                      id="occupation"
                      value={newAuthor.occupation}
                      onChange={(e) => setNewAuthor({...newAuthor, occupation: e.target.value})}
                      placeholder="Research Scientist"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="researcher_role">Researcher Role</Label>
                    <Input
                      id="researcher_role"
                      value={newAuthor.researcher_role}
                      onChange={(e) => setNewAuthor({...newAuthor, researcher_role: e.target.value})}
                      placeholder="PhD Student, Postdoc, Professor..."
                    />
                  </div>
                  
                  <div className="border-t pt-4">
                    <Label htmlFor="custom_password">Custom Password (optional)</Label>
                    <Input
                      id="custom_password"
                      type="password"
                      value={newAuthor.custom_password}
                      onChange={(e) => setNewAuthor({...newAuthor, custom_password: e.target.value})}
                      placeholder="Leave empty to auto-generate"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      If empty, a secure temporary password will be generated
                    </p>
                  </div>
                  
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={closeAddDialog}>Cancel</Button>
                    <Button onClick={handleAddAuthor} className="bg-green-600 hover:bg-green-700">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Create Author
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-900 mb-2">
                      ✅ Author created successfully!
                    </p>
                    <p className="text-sm text-green-700">
                      The temporary password has been generated. Make sure to copy it now - it won't be shown again!
                    </p>
                  </div>
                  
                  <div className="bg-slate-100 rounded-lg p-4">
                    <Label className="text-sm font-semibold mb-2 block">Temporary Password:</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border text-lg font-mono">
                        {tempPassword}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyPassword}
                        className={passwordCopied ? 'bg-green-100 border-green-300' : ''}
                      >
                        {passwordCopied ? (
                          <>
                            <Check className="h-4 w-4 mr-1 text-green-600" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> Send this password to the author securely. They will be able to change it after first login.
                    </p>
                  </div>
                  
                  <DialogFooter>
                    <Button onClick={closeAddDialog} className="w-full bg-blue-600 hover:bg-blue-700">
                      Done
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Edit Author Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          if (!open) closeEditDialog();
          setIsEditDialogOpen(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Edit Author: {editingAuthor?.username}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-username">Username *</Label>
                  <Input
                    id="edit-username"
                    value={editForm.username}
                    onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                    placeholder="johndoe"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-email">Email *</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    placeholder="john@university.edu"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit-first-name">First Name</Label>
                  <Input
                    id="edit-first-name"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-middle-name">Middle Name</Label>
                  <Input
                    id="edit-middle-name"
                    value={editForm.middle_name}
                    onChange={(e) => setEditForm({...editForm, middle_name: e.target.value})}
                    placeholder="M."
                  />
                </div>
                <div>
                  <Label htmlFor="edit-last-name">Last Name</Label>
                  <Input
                    id="edit-last-name"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-town">Town/City</Label>
                  <Input
                    id="edit-town"
                    value={editForm.town}
                    onChange={(e) => setEditForm({...editForm, town: e.target.value})}
                    placeholder="Sofia"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-country">Country *</Label>
                  <Input
                    id="edit-country"
                    value={editForm.country}
                    onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                    placeholder="Bulgaria"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-occupation">Occupation</Label>
                  <Input
                    id="edit-occupation"
                    value={editForm.occupation}
                    onChange={(e) => setEditForm({...editForm, occupation: e.target.value})}
                    placeholder="Medical Researcher"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-researcher-role">Researcher Role</Label>
                  <Input
                    id="edit-researcher-role"
                    value={editForm.researcher_role}
                    onChange={(e) => setEditForm({...editForm, researcher_role: e.target.value})}
                    placeholder="Principal Investigator"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <span>ℹ️</span>
                  <span>Fields marked with * are required. Password cannot be changed here.</span>
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={closeEditDialog}>
                Cancel
              </Button>
              <Button onClick={handleUpdateAuthor} className="bg-blue-600 hover:bg-blue-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Authors Table */}
        <Card>
          <CardHeader>
            <CardTitle>Authors ({filteredAuthors.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr className="text-left text-sm text-slate-600">
                    <th className="pb-3 font-semibold">Name</th>
                    <th className="pb-3 font-semibold">Email</th>
                    <th className="pb-3 font-semibold">Location</th>
                    <th className="pb-3 font-semibold">Occupation</th>
                    <th className="pb-3 font-semibold">Registered</th>
                    <th className="pb-3 font-semibold text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAuthors.map((author) => (
                    <tr key={author.id || author.user_id} className="border-b hover:bg-slate-50">
                      <td className="py-4">
                        <div>
                          <p className="font-semibold text-slate-900">{author.username}</p>
                          {(author.first_name || author.last_name) && (
                            <p className="text-sm text-slate-600">
                              {[author.first_name, author.middle_name, author.last_name]
                                .filter(Boolean)
                                .join(' ')}
                            </p>
                          )}
                          {author.alternative_names && author.alternative_names.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {author.alternative_names.slice(0, 2).map((name, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="h-4 w-4" />
                          {author.email}
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <MapPin className="h-4 w-4" />
                          {author.town && `${author.town}, `}{author.country}
                        </div>
                      </td>
                      <td className="py-4 text-sm text-slate-600">{author.occupation || 'N/A'}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="h-4 w-4" />
                          {new Date(author.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* Edit Button */}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditDialog(author)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          {/* Delete Button */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Author</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete <strong>{author.username}</strong>? 
                                This will also delete all their article claims and statistics. 
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAuthor(author.user_id || author.id, author.username)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredAuthors.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No authors found
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAuthors;
